import { Button, Drawer, Dropdown, Flex, Layout, Menu, message, Popconfirm, Tooltip, Typography } from "antd";
import { ApiService } from "api/api.service";
import LayoutHeader from "components/LayoutHeader";
import { SingleQueryWrapper } from "components/QueryWrapper";
import { RoleTypes } from "dtos/user.dto";
import { ReactNode, useEffect, useState } from "react";
import {
  TbArrowBackUp,
  TbDeviceMobile,
  TbDotsVertical,
  TbLayoutSidebar,
  TbLayoutSidebarFilled,
  TbLogout,
  TbMenuDeep,
  TbSwitch,
} from "react-icons/tb";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "store";
import { PROJECT_INFO } from "utils/constants";
import { useWindowWidth } from "utils/hooks";
import { storedTokens } from "utils/storedTokens";
import styles from "./DashboardLayout.module.scss";
import { useSidebarMenuItems } from "./DashboardLayout.utils";
import VerifyPhoneModal from "pages/Dashboard/components/VerifyPhoneModal";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { api } from "api";

function parseBoolean(str: string) {
  if (str.toLowerCase() === "true") return true;
  else return false;
}

function DashboardLayout(
  props: Readonly<{
    headerLeft?: ReactNode;
    headerRight?: ReactNode;
    children?: ReactNode;
    className?: string;
  }>,
) {
  const currentUser = ApiService.user.useGetCurrentUser();

  return (
    <SingleQueryWrapper
      query={currentUser}
      renderData={() => {
        return <DashboardLayoutInner {...props} />;
      }}
    />
  );
}

function findOpenKeys(items: ItemType<MenuItemType>[], targetKey: string, parents: string[] = []): string[] | null {
  for (const item of items) {
    if (!item) continue;
    if (item.key === targetKey) return parents;
    if ("children" in item && item.children && typeof item.key === "string") {
      const found = findOpenKeys(item.children, targetKey, [...parents, item.key]);
      if (found) return found;
    }
  }
  return null;
}

function DashboardLayoutInner({
  headerLeft,
  headerRight,
  children,
  // currentUser,
  className,
}: Readonly<{
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  children?: ReactNode;
  className?: string;
}>) {
  const { pathname } = useLocation();
  // const pathname = location.pathname;
  const pageTitle = useAppSelector((state) => state.pageTitle);
  const currentUser = useAppSelector((state) => state.user);
  const logout = ApiService.user.useLogout();

  const userRole = currentUser?.role;
  const windowWidth = useWindowWidth();
  const sideMenu = useSidebarMenuItems(userRole) ?? [];

  const [sidebarClosed, setSidebarClosed] = useState(parseBoolean(storedTokens.sidebarClosed.getToken() ?? "false"));
  const [open, setOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const unverifiedPhone = currentUser.phone?.number && currentUser.phone.code && !currentUser.phone.verified;
  const phoneLogin = ApiService.user.usePhoneLogin();

  const [openKeys, setOpenKeys] = useState<string[]>(() => findOpenKeys(sideMenu, pathname) ?? []);

  useEffect(() => {
    const parents = findOpenKeys(sideMenu, pathname);
    if (parents?.length) {
      setOpenKeys((prev) => Array.from(new Set([...prev, ...parents])));
    }
  }, [pathname]);

  function handleSidebarClose(collapse: boolean) {
    storedTokens.sidebarClosed.setToken(String(collapse));
    setSidebarClosed(collapse);
  }

  const loginAsOther = ApiService.user.useLoginAsOther();

  const menu = (
    <Flex style={{ height: "calc(100% - 80px)", flexDirection: "column" }} className={className}>
      <Menu
        theme="light"
        selectedKeys={[pathname]}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        items={sideMenu}
        mode="inline"
        style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}
      />
      <div className="h-fit flex flex-col 2xl:pb-0 gap-2 px-2 pt-2 shrink-0">
        {unverifiedPhone ? (
          <Popconfirm
            placement="leftTop"
            title={`OTP will be sent to +${currentUser.phone?.code} ${currentUser.phone?.number} for verification.`}
            okText="Proceed"
            cancelText="Cancel"
            onConfirm={() => {
              phoneLogin.mutate(
                {
                  phone: currentUser.phone?.number,
                  code: currentUser.phone?.code,
                  bypass: true,
                },
                {
                  onSuccess: () => {
                    message.success("OTP has been sent to your number!");
                    setIsModalOpen(true);
                  },
                },
              );
            }}
          >
            <Button variant="solid" color="red" type="primary" icon={<TbDeviceMobile />}>
              {sidebarClosed ? null : "Verify Phone"}
            </Button>
          </Popconfirm>
        ) : null}
        <div
          className="border border-solid border-gray-200 rounded-lg overflow-hidden"
          style={{
            opacity: sidebarClosed ? 0 : 1,
            width: sidebarClosed ? 0 : "100%",
            height: sidebarClosed ? 0 : "100%",
            transition: "opacity 150ms ease-in-out, width 150ms ease-in-out, height 150ms ease-in-out",
          }}
        >
          {!sidebarClosed && (
            <div className="py-1 px-2 flex justify-between items-center gap-2">
              <Flex vertical className="w-3/4">
                <Typography.Text>{currentUser?.name}</Typography.Text>
                <Typography.Text ellipsis className="text-xs text-gray-400">
                  {currentUser?.email}
                </Typography.Text>
              </Flex>
              <Dropdown
                placement="bottomRight"
                menu={{
                  items: (() => {
                    const items: any[] = [];

                    const recentUsersRaw = sessionStorage.getItem("recentUsers");
                    const recentUsers = recentUsersRaw ? JSON.parse(recentUsersRaw) : [];

                    if (Array.isArray(recentUsers) && recentUsers.length > 0) {
                      items.push({
                        key: "recent-users-label",
                        label: <strong>Recent Sessions</strong>,
                        disabled: true,
                      });

                      recentUsers.forEach((u: any, i: number) => {
                        items.push({
                          key: `recent-user-${i}`,
                          label: (
                            <div
                              className="flex justify-between items-center"
                              onClick={() => {
                                loginAsOther.mutate(
                                  { email: u.email },
                                  {
                                    onSuccess: () => {
                                      message.success(`Succesfully logged in as ${u.name}!`);
                                    },
                                  },
                                );
                              }}
                            >
                              <div>
                                <div className="font-medium text-sm">{u.name}</div>
                                <div className="text-xs text-gray-500">{u.email}</div>
                              </div>
                              <Tooltip title="Switch to this user">
                                <Button type="text" size="small" icon={<TbSwitch size={14} />} />
                              </Tooltip>
                            </div>
                          ),
                        });
                      });

                      items.push({ type: "divider" });
                    }

                    const hasAdminTokens =
                      !!localStorage.getItem("adminAccessToken") && !!localStorage.getItem("adminRefreshToken");

                    if (hasAdminTokens) {
                      items.push({
                        key: "switch-back",
                        label: "Switch Back",
                        icon: <TbArrowBackUp size={16} />,
                        onClick: async () => {
                          const adminAccess = localStorage.getItem("adminAccessToken");
                          const adminRefresh = localStorage.getItem("adminRefreshToken");

                          // if (adminAccess && adminRefresh) {
                          //   localStorage.setItem("accessToken", adminAccess);
                          //   localStorage.setItem("refreshToken", adminRefresh);

                          //   localStorage.removeItem("adminAccessToken");
                          //   localStorage.removeItem("adminRefreshToken");

                          //   globalThis.location.reload();
                          // }
                          if (adminAccess && adminRefresh) {
                            // ── NEW: Terminate the impersonated session before swapping ──
                            try {
                              await api.post("/auth/logout");
                            } catch {
                              // If it fails, still swap back — don't strand the admin
                            }
                            // ─────────────────────────────────────────────────────────────

                            localStorage.setItem("accessToken", adminAccess);
                            localStorage.setItem("refreshToken", adminRefresh);

                            localStorage.removeItem("adminAccessToken");
                            localStorage.removeItem("adminRefreshToken");

                            globalThis.location.reload();
                          }
                        },
                      });

                      items.push({ type: "divider" });
                    }

                    items.push({
                      key: "logout",
                      label: "Logout",
                      icon: <TbLogout size={16} />,
                      onClick: logout.mutate,
                    });

                    return items;
                  })(),
                }}
              >
                <Button type="text" icon={<TbDotsVertical size={18} />} />
              </Dropdown>
            </div>
          )}
        </div>
      </div>
      {isModalOpen && unverifiedPhone ? (
        <VerifyPhoneModal
          isModalOpen
          setIsModalOpen={setIsModalOpen}
          phoneDetails={{ phone: currentUser.phone?.number, code: currentUser.phone?.code }}
        />
      ) : null}
    </Flex>
  );

  const Humburger = () => {
    return (
      <div className={styles.menu_icon}>
        <TbMenuDeep style={{ cursor: "pointer", fontSize: "20px" }} onClick={() => setOpen(true)} />
        <Drawer
          width={200}
          title={<img style={{ height: 32, width: "auto" }} src={PROJECT_INFO.logo} alt="Logo" />}
          onClose={() => setOpen(false)}
          open={open}
        >
          {menu}
        </Drawer>
      </div>
    );
  };

  const token = storedTokens.accessToken.getToken();
  if (!userRole || (userRole && !Object.values(RoleTypes).includes(userRole)) || !token) {
    storedTokens.clear();
    return <Navigate to={`/`} />;
  }
  // if (firstMenu !== "/dashboard" && userRole) {
  //   return <Navigate to={MENU_ITEMS[userRole]?.[0]?.path} />;
  // }
  return (
    <Layout style={{ minHeight: "100vh", overflowY: "auto" }}>
      <Layout.Sider
        collapsible
        theme="light"
        collapsedWidth={50}
        collapsed={sidebarClosed}
        onCollapse={handleSidebarClose}
        trigger={
          sidebarClosed ? <TbLayoutSidebar className="anticon" /> : <TbLayoutSidebarFilled className="anticon" />
        }
        className={styles.sider}
      >
        <Flex justify="center" align="center" gap={8} style={{ margin: "24px 4px 24px" }}>
          <img
            style={{ height: 32, width: "auto" }}
            src={sidebarClosed ? PROJECT_INFO.favicon : PROJECT_INFO.logo}
            alt="Logo"
          />
        </Flex>
        {menu}
      </Layout.Sider>
      <Layout
        className="!bg-white"
        style={{
          marginLeft: windowWidth < 1024 ? 0 : sidebarClosed ? 50 : 200,
          transition: "margin 150ms ease-in-out",
        }}
      >
        <LayoutHeader
          pageTitle={pageTitle}
          headerLeft={headerLeft}
          headerWidth={windowWidth < 1024 ? "100%" : sidebarClosed ? "100%" : "calc(100% - 200px)"}
        >
          <div className="inline-flex gap-3 items-center">
            {headerRight}
            <Humburger />
          </div>
        </LayoutHeader>

        <Layout.Content
          className={styles.main_body}
          style={{
            height: `calc(100vh - ${PROJECT_INFO.layout.headerHeight})`,
            marginTop: PROJECT_INFO.layout.headerHeight,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {children}
        </Layout.Content>
      </Layout>
    </Layout>
  );
}

export default DashboardLayout;
